import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Card,
  Heading,
  Text,
  Flex,
  Container,
  Table,
  Button,
  Icon,
  Spacer,
  Box,
  Skeleton,
  Tabs,
  useBreakpointValue,
  Stack,
  Badge,
  Dialog,
} from '@chakra-ui/react';
import { NavLink, useParams, useNavigate } from 'react-router-dom';
import QuickSearchForm from '@/components/ui/quick-search-form';
import useAIContentRequests from '@/lib/hooks/use-ai-content-requests';
import { ChevronDownIcon, ChevronUpIcon, ListBulletIcon, PencilIcon, TrashIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { Prose } from '@/components/ui/prose';
import { toaster } from '@/components/ui/toaster';
import useIsInAdminGroup from '@/lib/hooks/use-is-in-admin-group';
import useIsCancelled from '@/lib/hooks/use-is-cancelled';
import useContentTypesStore from '@/utils/use-content-type';
import { AIContentRequest, ContentType, ContentTypeFieldView, BlockType } from '@/lib/types';
import ContentNamesStepsModal from '@/components/content-names-steps-modal';
import ManualFormModal from '@/components/ai-content-requests/manual-form-modal';
import api from '@/utils/api';

// Type for field options lookup: fieldReferenceId -> Map<optionValue, optionLabel>
type FieldOptionsMap = Map<string, Map<string, string>>;
// Global options map for looking up any option value across all fields
type GlobalOptionsMap = Map<string, string>;
// Field name map: referenceID -> fieldName
type FieldNameMap = Map<string, string>;

interface Props {
  request: AIContentRequest;
  onDelete: (request: AIContentRequest) => void;
  onEdit: (request: AIContentRequest) => void;
  onGenerate: (request: AIContentRequest) => void;
  fieldOptionsMap: FieldOptionsMap;
  globalOptionsMap: GlobalOptionsMap;
  fieldNameMap: FieldNameMap;
}

// Helper function to get the display value for a content field
// For selection fields, this returns the option label instead of the raw value
const getDisplayValue = (
  fieldKey: string,
  value: any,
  fieldOptionsMap: FieldOptionsMap,
  globalOptionsMap: GlobalOptionsMap
): string => {
  // First, try to look up by field key
  const options = fieldOptionsMap.get(fieldKey);

  // Handle array values (multi-select)
  if (Array.isArray(value)) {
    return value
      .map((v) => {
        const strVal = String(v);
        // Try field-specific options first, then global lookup
        if (options?.has(strVal)) return options.get(strVal);
        if (globalOptionsMap.has(strVal)) return globalOptionsMap.get(strVal);
        return strVal;
      })
      .join(', ');
  }

  // Handle single value
  const strVal = String(value);
  if (options?.has(strVal)) return options.get(strVal)!;
  if (globalOptionsMap.has(strVal)) return globalOptionsMap.get(strVal)!;

  return strVal;
};

// Check if a value looks like a selection option (numeric string or short identifier)
const isLikelySelectionValue = (value: any): boolean => {
  if (value === undefined || value === null) return false;
  const strVal = String(value);
  // Numeric values or short identifiers (less than 30 chars without HTML)
  return /^\d+$/.test(strVal) || (strVal.length < 30 && !strVal.includes('<') && !strVal.includes('\n'));
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const TableRow = ({ request, onDelete, onEdit, onGenerate, fieldOptionsMap, globalOptionsMap, fieldNameMap }: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // Parse sources if it's a string, otherwise use as-is
  const sources = useMemo(() => {
    if (!request.sources) return null;
    if (typeof request.sources === 'string') {
      try {
        return JSON.parse(request.sources);
      } catch (e) {
        console.error('Failed to parse sources:', e);
        return null;
      }
    }
    return request.sources;
  }, [request.sources]);

  // Render content value with option label lookup for selection fields
  const renderContentValue = (key: string, content: any) => {
    const value = content?.value;
    if (value === undefined || value === null) return null;

    // Get the field name from the map, fallback to content.label or key
    const fieldLabel = fieldNameMap.get(key) || content?.label || key;

    // Check if this field has options (is a selection field) OR if value looks like a selection option
    const options = fieldOptionsMap.get(key);
    const hasFieldOptions = options && options.size > 0;
    const valueInGlobalOptions = globalOptionsMap.has(String(value));

    if (hasFieldOptions || valueInGlobalOptions || isLikelySelectionValue(value)) {
      // Try to look up the display value
      const displayValue = getDisplayValue(key, value, fieldOptionsMap, globalOptionsMap);

      // If we found a different display value (meaning it was mapped), show it nicely
      if (displayValue !== String(value)) {
        return (
          <Box key={key} mb={3}>
            <Text fontSize="sm" fontWeight="bold" color="fg" mb={1}>
              {fieldLabel}
            </Text>
            <Text fontSize="sm" color="fg.muted">
              {displayValue}
            </Text>
          </Box>
        );
      }
    }

    // Not a selection field - render as HTML (for long text fields) or text
    if (typeof value === 'string' && (value.includes('<') || value.includes('&'))) {
      return (
        <Box key={key}>
          <Prose size='md'>
            <div dangerouslySetInnerHTML={{ __html: value }} />
          </Prose>
        </Box>
      );
    }

    return (
      <Box key={key} mb={3}>
        <Text fontSize="sm" fontWeight="bold" color="fg" mb={1}>
          {fieldLabel}
        </Text>
        <Text fontSize="sm" color="fg.muted">
          {String(value)}
        </Text>
      </Box>
    );
  };

  return (
    <>
      <Table.Row key={`${request.id}`}>
        <Table.Cell>
          <Flex alignItems="center" gap={2}>
            <Text
              color="fg"
              fontSize={'sm'}
              fontWeight={'medium'}
              maxW="200px"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {request.name}
            </Text>
            {request.creation_result === 'completed' && (
              <Button
                variant="ghost"
                size="xs"
                onClick={() => setIsExpanded(!isExpanded)}
                p={1}
              >
                <Icon as={isExpanded ? ChevronUpIcon : ChevronDownIcon} w={4} h={4} />
              </Button>
            )}
          </Flex>
        </Table.Cell>
        <Table.Cell>
          {request.creation_result ? (
            <Badge variant='surface' size='xs'>
              {request.creation_result}
            </Badge>
          ) : (
            <Button
              variant="outline"
              size="xs"
              py={1}
              px={3}
              onClick={async () => {
                setIsGenerating(true);
                try {
                  await onGenerate(request);
                } finally {
                  setIsGenerating(false);
                }
              }}
              loading={isGenerating}
              colorPalette="brand"
              title="Generate content"
            >
              <Icon as={SparklesIcon} w={4} h={4} />
              Generate AI
            </Button>
          )}
        </Table.Cell>
        <Table.Cell>
          <Text color="fg.subtle" fontSize={'sm'}>
            {request.description}
          </Text>
        </Table.Cell>
        <Table.Cell>
          <Text color="fg.subtle" fontSize={'sm'}>
            {formatDate(request.created_at)}
          </Text>
        </Table.Cell>
        <Table.Cell>
          <Flex gap={2}>
            {request.creation_result !== 'completed' && (
              <Button
                variant="ghost"
                size="xs"
                p={1}
                onClick={() => onEdit(request)}
              >
                <Icon as={PencilIcon} w={4} h={4} />
              </Button>
            )}
            <Button
              variant="ghost"
              size="xs"
              p={1}
              onClick={() => onDelete(request)}
            >
              <Icon as={TrashIcon} w={4} h={4} />
            </Button>
          </Flex>
        </Table.Cell>
      </Table.Row>
      {isExpanded && (
        <Table.Row key={`expanded-${request.id}`}>
          <Table.Cell colSpan={6}>
            <Box p={4} bg="bg.subtle" borderRadius="md">
              <Text fontSize="sm" fontWeight="medium" mb={2}>Request Content:</Text>
              <Text fontSize="sm" color="fg.muted" mb={4}>
                {request.created_description}
              </Text>

              <Box mb={4}>
                {request.created_content && (
                  <>
                    {Array.isArray(request.created_content) ? (
                      request.created_content.map((content: any, index: number) => (
                        renderContentValue(String(index), content)
                      ))
                    ) : typeof request.created_content === 'object' ? (
                      Object.entries(request.created_content).map(([key, content]: [string, any]) => (
                        renderContentValue(key, content)
                      ))
                    ) : (
                      <Box>
                        <Prose size='md'>
                          <div dangerouslySetInnerHTML={{ __html: request.created_content as string }} />
                        </Prose>
                      </Box>
                    )}
                  </>
                )}
              </Box>

              {((request.ai_prompt_content != null && request.ai_prompt_content !== '') ||
                (request.ai_prompt_image != null && request.ai_prompt_image !== '')) && (
                  <Box mb={4} pt={4} borderTop="1px solid" borderColor="border">
                    {request.ai_prompt_content != null && request.ai_prompt_content !== '' && (
                      <Box mb={3}>
                        <Text fontSize="xs" fontWeight="semibold" color="fg.muted" mb={2} textTransform="uppercase">
                          AI Prompt Content
                        </Text>
                        <Box
                          p={3}
                          bg="bg.panel"
                          borderRadius="md"
                          border="1px solid"
                          borderColor="border"
                          maxH="300px"
                          overflowY="auto"
                        >
                          <Text fontSize="xs" color="fg" whiteSpace="pre-wrap" fontFamily="mono">
                            {request.ai_prompt_content}
                          </Text>
                        </Box>
                      </Box>
                    )}
                    {request.ai_prompt_image != null && request.ai_prompt_image !== '' && (
                      <Box>
                        <Text fontSize="xs" fontWeight="semibold" color="fg.muted" mb={2} textTransform="uppercase">
                          AI Prompt Image
                        </Text>
                        <Box
                          p={3}
                          bg="bg.panel"
                          borderRadius="md"
                          border="1px solid"
                          borderColor="border"
                          maxH="300px"
                          overflowY="auto"
                        >
                          <Text fontSize="xs" color="fg" whiteSpace="pre-wrap" fontFamily="mono">
                            {request.ai_prompt_image}
                          </Text>
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}

              {sources && (
                <Box mt={4} pt={4} borderTop="1px solid" borderColor="border">
                  <Text fontSize="sm" fontWeight="medium" mb={3}>Sources:</Text>

                  {sources.synthesized_answer && (
                    <Box mb={4}>
                      <Text fontSize="xs" fontWeight="semibold" color="fg.muted" mb={1} textTransform="uppercase">
                        Synthesized Answer
                      </Text>
                      <Text fontSize="sm" color="fg" whiteSpace="pre-wrap">
                        {sources.synthesized_answer}
                      </Text>
                    </Box>
                  )}

                  {sources.chunks && sources.chunks.length > 0 && (
                    <Box mb={4}>
                      <Text fontSize="xs" fontWeight="semibold" color="fg.muted" mb={2} textTransform="uppercase">
                        Chunks ({sources.chunks.length})
                      </Text>
                      <Stack gap={2}>
                        {sources.chunks.map((chunk: string, index: number) => (
                          <Box key={index} p={3} bg="bg.panel" borderRadius="md" border="1px solid" borderColor="border">
                            <Text fontSize="xs" color="fg.muted" mb={1}>Chunk {index + 1}</Text>
                            <Text fontSize="sm" color="fg" whiteSpace="pre-wrap">
                              {chunk}
                            </Text>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {sources.documents && sources.documents.length > 0 && (
                    <Box>
                      <Text fontSize="xs" fontWeight="semibold" color="fg.muted" mb={2} textTransform="uppercase">
                        Documents ({sources.documents.length})
                      </Text>
                      <Stack gap={1}>
                        {sources.documents.map((document: string, index: number) => (
                          <Text key={index} fontSize="sm" color="fg">
                            • {document}
                          </Text>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Table.Cell>
        </Table.Row>
      )}
    </>
  );
};

const MobileRequestCard = ({ request, onDelete, onEdit, onGenerate, fieldOptionsMap, globalOptionsMap, fieldNameMap }: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // Parse sources if it's a string, otherwise use as-is
  const sources = useMemo(() => {
    if (!request.sources) return null;
    if (typeof request.sources === 'string') {
      try {
        return JSON.parse(request.sources);
      } catch (e) {
        console.error('Failed to parse sources:', e);
        return null;
      }
    }
    return request.sources;
  }, [request.sources]);

  // Render content value with option label lookup for selection fields
  const renderContentValue = (key: string, content: any) => {
    const value = content?.value;
    if (value === undefined || value === null) return null;

    // Get the field name from the map, fallback to content.label or key
    const fieldLabel = fieldNameMap.get(key) || content?.label || key;

    // Check if this field has options (is a selection field) OR if value looks like a selection option
    const options = fieldOptionsMap.get(key);
    const hasFieldOptions = options && options.size > 0;
    const valueInGlobalOptions = globalOptionsMap.has(String(value));

    if (hasFieldOptions || valueInGlobalOptions || isLikelySelectionValue(value)) {
      // Try to look up the display value
      const displayValue = getDisplayValue(key, value, fieldOptionsMap, globalOptionsMap);

      // If we found a different display value (meaning it was mapped), show it nicely
      if (displayValue !== String(value)) {
        return (
          <Box key={key} mb={3}>
            <Text fontSize="sm" fontWeight="bold" color="fg" mb={1}>
              {fieldLabel}
            </Text>
            <Text fontSize="sm" color="fg.muted">
              {displayValue}
            </Text>
          </Box>
        );
      }
    }

    // Not a selection field - render as HTML (for long text fields) or text
    if (typeof value === 'string' && (value.includes('<') || value.includes('&'))) {
      return (
        <Box key={key}>
          <Prose size='md'>
            <div dangerouslySetInnerHTML={{ __html: value }} />
          </Prose>
        </Box>
      );
    }

    return (
      <Box key={key} mb={3}>
        <Text fontSize="sm" fontWeight="bold" color="fg" mb={1}>
          {fieldLabel}
        </Text>
        <Text fontSize="sm" color="fg.muted">
          {String(value)}
        </Text>
      </Box>
    );
  };

  return (
    <Card.Root>
      <Card.Body p={4}>
        <Stack gap={3}>
          <Flex justify="space-between" align="flex-start" gap={2}>
            <Text
              color="fg"
              fontSize="md"
              fontWeight="medium"
              flex={1}
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {request.name}
            </Text>
            <Flex gap={1}>
              {request.creation_result !== 'completed' && (
                <Button
                  variant="ghost"
                  size="sm"
                  p={1}
                  onClick={() => onEdit(request)}
                >
                  <Icon as={PencilIcon} w={4} h={4} />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                p={1}
                onClick={() => onDelete(request)}
              >
                <Icon as={TrashIcon} w={4} h={4} />
              </Button>
            </Flex>
          </Flex>

          <Flex justify="space-between" align="center" gap={2}>
            {request.creation_result ? (
              <Badge variant='surface' size='sm'>
                {request.creation_result}
              </Badge>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  setIsGenerating(true);
                  try {
                    await onGenerate(request);
                  } finally {
                    setIsGenerating(false);
                  }
                }}
                loading={isGenerating}
                colorPalette="brand"
                title="Generate content"
              >
                <Icon as={SparklesIcon} w={4} h={4} />
                Generate AI
              </Button>
            )}
            <Text color="fg.subtle" fontSize="sm">
              {formatDate(request.created_at)}
            </Text>
          </Flex>

          {request.description && (
            <Text
              color="fg.muted"
              fontSize="sm"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {request.description}
            </Text>
          )}

          {request.creation_result === 'completed' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              alignSelf="flex-start"
            >
              <Icon as={isExpanded ? ChevronUpIcon : ChevronDownIcon} w={4} h={4} mr={1} />
              {isExpanded ? 'Hide' : 'Show'} Content
            </Button>
          )}
        </Stack>

        {isExpanded && (
          <Box mt={4} p={4} bg="bg.subtle" borderRadius="md">
            <Text fontSize="sm" fontWeight="medium" mb={2}>Request Content:</Text>
            <Text fontSize="sm" color="fg.muted" mb={4}>
              {request.created_description}
            </Text>

            <Box mb={4}>
              {request.created_content && (
                <>
                  {Array.isArray(request.created_content) ? (
                    request.created_content.map((content: any, index: number) => (
                      renderContentValue(String(index), content)
                    ))
                  ) : typeof request.created_content === 'object' ? (
                    Object.entries(request.created_content).map(([key, content]: [string, any]) => (
                      renderContentValue(key, content)
                    ))
                  ) : (
                    <Box>
                      <Prose size='md'>
                        <div dangerouslySetInnerHTML={{ __html: request.created_content as string }} />
                      </Prose>
                    </Box>
                  )}
                </>
              )}
            </Box>

            {sources && (
              <Box mt={4} pt={4} borderTop="1px solid" borderColor="border">
                <Text fontSize="sm" fontWeight="medium" mb={3}>Sources:</Text>

                {sources.synthesized_answer && (
                  <Box mb={4}>
                    <Text fontSize="xs" fontWeight="semibold" color="fg.muted" mb={1} textTransform="uppercase">
                      Synthesized Answer
                    </Text>
                    <Text fontSize="sm" color="fg" whiteSpace="pre-wrap">
                      {sources.synthesized_answer}
                    </Text>
                  </Box>
                )}

                {sources.chunks && sources.chunks.length > 0 && (
                  <Box mb={4}>
                    <Text fontSize="xs" fontWeight="semibold" color="fg.muted" mb={2} textTransform="uppercase">
                      Chunks ({sources.chunks.length})
                    </Text>
                    <Stack gap={2}>
                      {sources.chunks.map((chunk: string, index: number) => (
                        <Box key={index} p={3} bg="bg.panel" borderRadius="md" border="1px solid" borderColor="border">
                          <Text fontSize="xs" color="fg.muted" mb={1}>Chunk {index + 1}</Text>
                          <Text fontSize="sm" color="fg" whiteSpace="pre-wrap">
                            {chunk}
                          </Text>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}

                {sources.documents && sources.documents.length > 0 && (
                  <Box>
                    <Text fontSize="xs" fontWeight="semibold" color="fg.muted" mb={2} textTransform="uppercase">
                      Documents ({sources.documents.length})
                    </Text>
                    <Stack gap={1}>
                      {sources.documents.map((document: string, index: number) => (
                        <Text key={index} fontSize="sm" color="fg">
                          • {document}
                        </Text>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}
      </Card.Body>
    </Card.Root>
  );
};

export default function ListAIContentRequests() {
  const { workspaceId, contentTypeId } = useParams<{ workspaceId: string, contentTypeId: string }>();
  const navigate = useNavigate();
  const { contentTypes } = useContentTypesStore();
  const isAdmin = useIsInAdminGroup();
  const isCancelled = useIsCancelled();
  const [offset, setOffset] = useState(0);
  const [contentType, setContentType] = useState<ContentType>();
  const [allRequests, setAllRequests] = useState<AIContentRequest[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<AIContentRequest[]>([]);
  const [searchResults, setSearchResults] = useState<AIContentRequest[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isManualModalOpen, setIsManualModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState<AIContentRequest | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [fieldOptionsMap, setFieldOptionsMap] = useState<FieldOptionsMap>(new Map());
  const [globalOptionsMap, setGlobalOptionsMap] = useState<GlobalOptionsMap>(new Map());
  const [fieldNameMap, setFieldNameMap] = useState<FieldNameMap>(new Map());
  const isSmallScreen = useBreakpointValue({ base: true, md: false });

  useEffect(() => {
    if (contentTypeId) {
      setContentType(contentTypes.find((type) => type.id === parseInt(contentTypeId)));
    }
  }, [contentTypes, contentTypeId]);

  // Fetch content type fields to get selection options
  const fetchContentTypeFields = useCallback(async () => {
    if (!contentTypeId || !workspaceId) return;

    try {
      // Build the field options map for selection fields
      const optionsMap: FieldOptionsMap = new Map();
      // Also build a global map that maps any option value to its label
      const globalMap: GlobalOptionsMap = new Map();
      // Build field name map: referenceID -> field_name
      const nameMap: FieldNameMap = new Map();

      // Fetch content type fields
      const fieldsResponse = await api.get(
        `content-type-fields/${contentTypeId}/${workspaceId}/data`
      );
      const fields: ContentTypeFieldView[] = fieldsResponse.data.data || [];

      fields.forEach((field: any) => {
        // Store field name mapping
        if (field.referenceID && field.field_name) {
          nameMap.set(field.referenceID, field.field_name);
        }

        // Handle standard Selection fields with field_options
        if (field.field_type === BlockType.Selection && field.field_options?.length > 0) {
          const options = new Map<string, string>();
          field.field_options.forEach((opt: any) => {
            options.set(opt.option_value, opt.option_label);
            // Also add to global map for fallback lookup
            globalMap.set(opt.option_value, opt.option_label);
          });
          optionsMap.set(field.referenceID, options);
          // Also map by field_unique_name in case that's used as key
          if (field.field_unique_name) {
            optionsMap.set(field.field_unique_name, options);
          }
        }
      });

      setFieldOptionsMap(optionsMap);
      setGlobalOptionsMap(globalMap);
      setFieldNameMap(nameMap);
    } catch (err) {
      console.error('Failed to fetch content type fields:', err);
    }
  }, [contentTypeId, workspaceId]);

  useEffect(() => {
    fetchContentTypeFields();
  }, [fetchContentTypeFields]);

  const {
    requests,
    isLoading,
    hasMore,
  } = useAIContentRequests({
    workspaceId: workspaceId || '',
    limit: 50,
    offset,
    searchQuery: '',
  });

  useEffect(() => {
    if (offset === 0) {
      setAllRequests(requests);
    } else {
      setAllRequests(prev => [...prev, ...requests]);
    }
  }, [requests, offset]);

  useEffect(() => {
    setFilteredRequests(allRequests);
    setSearchResults(allRequests);
  }, [allRequests]);

  const handleLoadMore = () => {
    if (hasMore && !isLoading) {
      setOffset(prev => prev + 50);
    }
  };

  const handleSearchResults = (search: string) => {
    const filtered = filteredRequests.filter((request) => {
      return (
        (request.name && request.name.toLowerCase().includes(search.toLowerCase())) ||
        (request.description && request.description.toLowerCase().includes(search.toLowerCase())) ||
        (request.content_type && request.content_type.toLowerCase().includes(search.toLowerCase()))
      );
    });
    setSearchResults(filtered);
  };

  const handleGenerateContent = async () => {
    if (!workspaceId) {
      return;
    }
    try {
      await api.post(`/ai-content-requests/generate`, {
        workspace_id: parseInt(workspaceId),
        assistant_name: 'genai',
      });
      toaster.create({
        title: 'Workflow was started!',
        type: 'success',
      });
    } catch (err: any) {
      toaster.create({
        title: err.response?.data?.error || 'Failed to generate content',
        type: 'error',
      });
    } finally {
      setIsModalOpen(false);
    }
  };

  const handleGenerateRequest = async () => {
    if (!workspaceId) {
      return;
    }
    try {
      await api.post(`/ai-content-requests/generate`, {
        workspace_id: parseInt(workspaceId),
        assistant_name: 'genai',
      });
      toaster.create({
        title: 'Content generation started!',
        type: 'success',
      });
    } catch (err: any) {
      toaster.create({
        title: err.response?.data?.error || 'Failed to generate content',
        type: 'error',
      });
    }
  };

  const handleDeleteRequest = async () => {
    if (!requestToDelete?.id) {
      return;
    }

    setIsDeleting(true);
    try {
      await api.delete(`/ai-content-requests/${requestToDelete.id}`);
      toaster.create({
        title: 'AI Content Request deleted successfully',
        type: 'success',
      });

      // Remove the deleted request from all state arrays
      setAllRequests(prev => prev.filter(req => req.id !== requestToDelete.id));
      setFilteredRequests(prev => prev.filter(req => req.id !== requestToDelete.id));
      setSearchResults(prev => prev.filter(req => req.id !== requestToDelete.id));
    } catch (err: any) {
      toaster.create({
        title: err.response?.data?.error || 'Failed to delete AI content request',
        type: 'error',
      });
    } finally {
      setIsDeleting(false);
      setDeleteModalOpen(false);
      setRequestToDelete(null);
    }
  };

  const openDeleteModal = (request: AIContentRequest) => {
    setRequestToDelete(request);
    setDeleteModalOpen(true);
  };

  const handleEdit = (request: AIContentRequest) => {
    navigate(`/${workspaceId}/content-types/${contentTypeId}/ai-content-requests/${request.id}/edit`);
  };

  return (
    <Card.Root minH={'100vh'} mx={'auto'} width={{ md: '90%' }} maxW={'6xl'}>
      <Card.Header>
        <Container>
          <Stack gap={4} mb={4}>
            <Flex direction={{ base: 'column', md: 'row' }} align={{ base: 'stretch', md: 'center' }} gap={4}>
              <Heading size={{ base: '2xl', md: '3xl' }}>{contentType && contentType.name}</Heading>
              <Spacer />
              <Box display={{ base: 'block', md: 'none' }} w={'full'}>
                <QuickSearchForm handleSearch={handleSearchResults} />
              </Box>
              <Box display={{ base: 'none', md: 'block' }} w={'50%'}>
                <QuickSearchForm handleSearch={handleSearchResults} />
              </Box>
              <Spacer />
              {isAdmin && !isCancelled && (
                <Stack direction={{ base: 'column', md: 'row' }} gap={2} align={{ base: 'stretch', md: 'flex-end' }}>
                  <Button
                    colorPalette='brand'
                    variant='outline'
                    onClick={() => setIsModalOpen(true)}
                    size={{ base: 'md', md: 'sm' }}
                    width={{ base: 'full', md: 'auto' }}
                  >
                    <Icon as={ListBulletIcon} w={4} h={4} />
                    <Text display={{ base: 'none', sm: 'inline' }}>Create AI Content Requests</Text>
                    <Text display={{ base: 'inline', sm: 'none' }}>Create Requests</Text>
                  </Button>
                  <Button
                    colorPalette='brand'
                    variant='solid'
                    onClick={() => setIsManualModalOpen(true)}
                    size={{ base: 'md', md: 'sm' }}
                    width={{ base: 'full', md: 'auto' }}
                  >
                    <Icon as={PencilIcon} w={4} h={4} />
                    <Text display={{ base: 'none', sm: 'inline' }}>Manual Form</Text>
                    <Text display={{ base: 'inline', sm: 'none' }}>Manual</Text>
                  </Button>
                </Stack>
              )}
            </Flex>
          </Stack>
        </Container>
      </Card.Header>
      <Card.Body>
        <Container>
          <Stack gap={3}>
            <Tabs.Root
              value={'ai-content-requests'}
              orientation={isSmallScreen ? 'vertical' : 'horizontal'}
              variant={isSmallScreen ? 'enclosed' : 'line'}
              size={{ base: 'md', md: 'lg' }}
            >
              <Tabs.List
                width={isSmallScreen ? 'full' : 'auto'}
                gap={{ base: 1, md: 0 }}
                flexWrap={{ base: 'wrap', md: 'nowrap' }}
              >
                <Tabs.Trigger value='workspace-library' asChild>
                  <NavLink
                    to={`/${workspaceId}/content-types/${contentTypeId}/contents/workspace-library`}
                  >
                    <Text display={{ base: 'none', sm: 'inline' }}>Workspace Library</Text>
                    <Text display={{ base: 'inline', sm: 'none' }}>Workspace</Text>
                  </NavLink>
                </Tabs.Trigger>
                <Tabs.Trigger value='partner-libraries' asChild>
                  <NavLink
                    to={`/${workspaceId}/content-types/${contentTypeId}/contents/partner-libraries`}
                  >
                    <Text display={{ base: 'none', sm: 'inline' }}>Partner Libraries</Text>
                    <Text display={{ base: 'inline', sm: 'none' }}>Partner</Text>
                  </NavLink>
                </Tabs.Trigger>
                {isAdmin && (
                  <Tabs.Trigger value='my-library' asChild>
                    <NavLink
                      to={`/${workspaceId}/content-types/${contentTypeId}/contents/my-library`}
                    >
                      My Library
                    </NavLink>
                  </Tabs.Trigger>
                )}
                <Tabs.Trigger value='ai-content-requests' asChild>
                  <NavLink
                    to={`/${workspaceId}/content-types/${contentTypeId}/ai-content-requests`}
                  >
                    <Text display={{ base: 'none', sm: 'inline' }}>AI Content Requests</Text>
                    <Text display={{ base: 'inline', sm: 'none' }}>AI Requests</Text>
                  </NavLink>
                </Tabs.Trigger>
              </Tabs.List>
            </Tabs.Root>

            <Box display={{ base: 'none', md: 'block' }}>
              <Table.Root>
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader color='fg.muted'>Name</Table.ColumnHeader>
                    <Table.ColumnHeader color='fg.muted'>Status</Table.ColumnHeader>
                    <Table.ColumnHeader color='fg.muted'>Description</Table.ColumnHeader>
                    <Table.ColumnHeader color='fg.muted'>Created</Table.ColumnHeader>
                    <Table.ColumnHeader color='fg.muted'>Actions</Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {isLoading && offset === 0 ? (
                    Array.from({ length: 5 }).map((_, index) => (
                      <Table.Row key={index}>
                        <Table.Cell><Skeleton height="20px" /></Table.Cell>
                        <Table.Cell><Skeleton height="20px" /></Table.Cell>
                        <Table.Cell><Skeleton height="20px" /></Table.Cell>
                        <Table.Cell><Skeleton height="20px" /></Table.Cell>
                        <Table.Cell><Skeleton height="20px" /></Table.Cell>
                      </Table.Row>
                    ))
                  ) : (
                    searchResults.map((request, index) => (
                      <TableRow key={request.id || index} request={request} onDelete={openDeleteModal} onEdit={handleEdit} onGenerate={handleGenerateRequest} fieldOptionsMap={fieldOptionsMap} globalOptionsMap={globalOptionsMap} fieldNameMap={fieldNameMap} />
                    ))
                  )}
                </Table.Body>
              </Table.Root>
            </Box>

            <Box display={{ base: 'block', md: 'none' }}>
              <Stack gap={3}>
                {isLoading && offset === 0 ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <Card.Root key={index} p={4}>
                      <Stack gap={2}>
                        <Skeleton height="20px" />
                        <Skeleton height="16px" />
                        <Skeleton height="16px" />
                      </Stack>
                    </Card.Root>
                  ))
                ) : (
                  searchResults.map((request, index) => (
                    <MobileRequestCard
                      key={request.id || index}
                      request={request}
                      onDelete={openDeleteModal}
                      onEdit={handleEdit}
                      onGenerate={handleGenerateRequest}
                      fieldOptionsMap={fieldOptionsMap}
                      globalOptionsMap={globalOptionsMap}
                      fieldNameMap={fieldNameMap}
                    />
                  ))
                )}
              </Stack>
            </Box>

            {hasMore && !isLoading && (
              <Flex justify="center" mt={6}>
                <Button
                  colorPalette='brand'
                  variant='outline'
                  onClick={handleLoadMore}
                >
                  Load More
                </Button>
              </Flex>
            )}

            {isLoading && offset > 0 && (
              <Flex justify="center" mt={4}>
                <Text color="fg.muted">Loading more requests...</Text>
              </Flex>
            )}

            {!isLoading && searchResults.length === 0 && (
              <Flex justify="center" mt={8}>
                <Text color="fg.muted">No AI content requests found</Text>
              </Flex>
            )}
          </Stack>
        </Container>
      </Card.Body>

      {/* Content Names Steps Modal */}
      <ContentNamesStepsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        workspaceId={workspaceId || ''}
        contentTypeId={contentTypeId || ''}
        onSuccess={() => {
          handleGenerateContent();
        }}
      />

      {/* Manual Form Modal */}
      <ManualFormModal
        isOpen={isManualModalOpen}
        onClose={() => setIsManualModalOpen(false)}
        workspaceId={workspaceId || ''}
        contentTypeId={contentTypeId || ''}
        onSuccess={() => {
          handleGenerateContent();
        }}
      />

      {/* Delete Confirmation Modal */}
      <Dialog.Root open={deleteModalOpen} onOpenChange={(e: { open: boolean }) => setDeleteModalOpen(e.open)}>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>Delete AI Content Request</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <Text>Are you sure you want to delete "{requestToDelete?.name}"? This action cannot be undone.</Text>
            </Dialog.Body>
            <Dialog.Footer>
              <Button
                variant="outline"
                onClick={() => {
                  setDeleteModalOpen(false);
                  setRequestToDelete(null);
                }}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                variant='danger'
                onClick={handleDeleteRequest}
                loading={isDeleting}
              >
                Delete
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
    </Card.Root>
  );
}
